// Chat data access. Thin wrappers over the SDK adapter so the wire
// shape stays consistent with the rest of the workspace surface
// (sdk → adapter → workspace-project worker → Supabase). Realtime
// subscriptions stay on the direct Supabase client (`subscribeMessages`
// below) because the worker doesn't proxy WebSocket channels — that's
// the documented split per shared/functions/chat/subscribeRealtime.js.
//
// Migrated 2026-04-30 from direct `chatClient()` reads to
// `getSDK().execute('workspaceProject.chat[.X]', '<method>', ...)`.
// Direct Supabase reads bypassed the worker auth / RLS and meant the
// audit saw 0 calls to /workspace-project/* — see AUDIT_PLAN.md.

import { chatClient } from './client.js'
import { getSDK } from '../../../functions/sdk.js'
import { CHAT_CONFIG } from './config.js'

const { tables } = CHAT_CONFIG

// Normalize SDK envelope. `execute()` returns either a bare array/row
// or `{ data, error }`-shaped — adapters vary across surfaces. Squash
// to `{ data, error }` so consumers don't branch on shape.
const _envelope = (raw) => {
  if (raw == null) return { data: [], error: null }
  if (raw && typeof raw === 'object' && ('data' in raw || 'error' in raw)) {
    return { data: raw.data ?? [], error: raw.error ?? null }
  }
  return { data: raw, error: null }
}

// List channels the caller can see, enriched with member emails so
// DM / group-DM channels can render human-readable names without a
// second round-trip in the UI. `_memberEmails` is an array attached
// to each channel; use `channelDisplayName(channel, peopleByEmail,
// currentUserEmail)` to format.
//
// RLS on governance restricts to the active workspace via the
// workspace-project worker's scoped client.
export const listChannels = async () => {
  const sdk = getSDK?.()
  if (!sdk?.execute) return { data: [], error: null }
  try {
    const [channelsRaw, membersRaw] = await Promise.all([
      sdk.execute('workspaceProject.chat', 'list'),
      sdk.execute('workspaceProject.chat.members', 'list')
    ])
    const channelsRes = _envelope(channelsRaw)
    const membersRes = _envelope(membersRaw)
    if (channelsRes.error) return channelsRes
    const memberMap = new Map()
    for (const m of membersRes.data || []) {
      if (!memberMap.has(m.channel_id)) memberMap.set(m.channel_id, [])
      memberMap.get(m.channel_id).push(m.member_email)
    }
    const data = (channelsRes.data || [])
      .slice()
      .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
      .map((ch) => ({ ...ch, _memberEmails: memberMap.get(ch.id) || [] }))
    return { data, error: null }
  } catch (error) {
    return { data: [], error }
  }
}

// Resolve a human-readable name for a channel. Mirrors intranet's
// `channelDisplayName` but works off the `_memberEmails` array that
// `listChannels` attaches, so callers don't need to pass a members
// array themselves.
//   - kind='channel' → `#name`
//   - kind='dm' → first other member's name (from peopleByEmail) or email
//   - kind='group_dm' → comma-joined other members' names
//
// `peopleByEmail` is optional — when absent, falls back to email.
export const channelDisplayName = (channel, currentUserEmail, peopleByEmail = {}) => {
  if (!channel) return ''
  if (channel.kind === 'channel') return `#${channel.name || 'channel'}`
  const others = (channel._memberEmails || [])
    .filter((e) => e !== currentUserEmail)
    .map((e) => peopleByEmail[e]?.name || e)
  if (channel.kind === 'dm') return others[0] || 'Direct message'
  return others.join(', ') || 'Group DM'
}

// Paginated message load for a channel. `beforeId` is the cursor;
// caller passes the earliest known message id to load older messages.
// Now routes through the SDK adapter so RLS + workspace scoping
// happen server-side — same as the rest of the chat surface.
export const listMessages = async ({ channelId, beforeId, limit = 50 } = {}) => {
  const sdk = getSDK?.()
  if (!sdk?.execute) return { data: [], error: null }
  try {
    const raw = await sdk.execute('workspaceProject.chat.messages', 'list', {
      channelId,
      options: {
        limit,
        order: { column: 'created_at', ascending: false },
        ...(beforeId
          ? { filter: { column: 'id', op: 'lt', value: beforeId } }
          : {})
      }
    })
    return _envelope(raw)
  } catch (error) {
    return { data: [], error }
  }
}

// Send a new message. Returns the inserted row (or error).
// In Phase 3 post-migration, workspace_id defaults to the active
// workspace via the column DEFAULT — caller never needs to pass it.
export const sendMessage = async ({ channelId, senderEmail, body, parentId = null }) => {
  const sdk = getSDK?.()
  if (!sdk?.execute) return { data: null, error: new Error('sdk unavailable') }
  try {
    const raw = await sdk.execute('workspaceProject.chat.messages', 'create', {
      channelId,
      payload: {
        sender_email: senderEmail,
        body,
        parent_id: parentId
      }
    })
    return _envelope(raw)
  } catch (error) {
    return { data: null, error }
  }
}

// Soft-delete (mark `deleted_at`). The `remove` SDK method maps to
// DELETE /chat/messages/:id which the worker handles as a soft delete
// (sets deleted_at, preserves the row for audit + thread integrity).
export const deleteMessage = async (messageId) => {
  const sdk = getSDK?.()
  if (!sdk?.execute) return { data: null, error: new Error('sdk unavailable') }
  try {
    const raw = await sdk.execute('workspaceProject.chat.messages', 'remove', {
      messageId
    })
    return _envelope(raw)
  } catch (error) {
    return { data: null, error }
  }
}

// Subscribe to new messages via Supabase realtime. `onInsert`
// fires with the new row for every INSERT. Returns an unsubscribe
// function. Filter narrows to a specific channel_id when provided.
export const subscribeMessages = ({ channelId, onInsert, onUpdate, onDelete } = {}) => {
  const sb = chatClient()
  if (!sb) return () => {}
  const name = `rt-chat-${channelId || 'all'}-${Math.random().toString(36).slice(2, 8)}`
  const ch = sb.channel(name)
  const filter = channelId ? `channel_id=eq.${channelId}` : undefined
  ch.on('postgres_changes',
    { event: 'INSERT', schema: 'public', table: tables.messages, filter },
    (payload) => onInsert?.(payload.new))
  if (onUpdate) {
    ch.on('postgres_changes',
      { event: 'UPDATE', schema: 'public', table: tables.messages, filter },
      (payload) => onUpdate(payload.new))
  }
  if (onDelete) {
    ch.on('postgres_changes',
      { event: 'DELETE', schema: 'public', table: tables.messages, filter },
      (payload) => onDelete(payload.old))
  }
  ch.subscribe()
  return () => { try { ch.unsubscribe() } catch {} }
}

// Count unread messages across every channel for the current user.
// Pure; inputs are app-provided. Used by the Messenger icon's unread
// badge + any notification summary UI.
export const unreadCount = ({ messagesByChannel, channelMembers, currentUserEmail }) => {
  if (!messagesByChannel || !channelMembers || !currentUserEmail) return 0
  const myMemberships = channelMembers.filter((m) => m.member_email === currentUserEmail)
  let total = 0
  for (const mm of myMemberships) {
    if (mm.muted) continue
    const cutoff = mm.last_read_at ? new Date(mm.last_read_at).getTime() : 0
    const msgs = messagesByChannel[mm.channel_id] || []
    for (const msg of msgs) {
      if (msg.deleted) continue
      const author = msg.authorEmail ?? msg.author_email ?? msg.sender_email
      if (author === currentUserEmail) continue
      const t = new Date(msg.createdAt ?? msg.created_at ?? 0).getTime()
      if (t > cutoff) total++
    }
  }
  return total
}
